import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { sanitizeHtml } from "@/utils/sanitize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public, unauthenticated read-only render of a PUBLISHED lesson plan.
// Deliberately unstyled placeholder — full instructor view is a future update.
export default function PublicLessonPlan() {
  const { id } = useParams();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/lesson-plans/${id}`)
      .then(({ data }) => setLesson(data))
      .catch((err) => setError(err.response?.data?.detail || "Unable to load lesson plan"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Loader2 size={24} style={{ animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 720, margin: "60px auto", padding: "0 20px", fontFamily: "system-ui, sans-serif" }}>
        <h1>Lesson Plan Unavailable</h1>
        <p style={{ color: "#64748b" }}>{error}</p>
      </div>
    );
  }

  if (!lesson) return null;

  return (
    <div
      style={{ maxWidth: 820, margin: "40px auto", padding: "0 20px", fontFamily: "system-ui, sans-serif", color: "#0f172a" }}
      data-testid="public-lesson-plan"
    >
      <header style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 16, marginBottom: 24 }}>
        <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b" }}>Lesson Plan</div>
        <h1 style={{ fontSize: 32, fontWeight: 700, margin: "6px 0 6px" }} data-testid="public-lesson-title">{lesson.title}</h1>
        {lesson.description && <p style={{ color: "#475569", margin: 0 }}>{lesson.description}</p>}
      </header>

      {(lesson.topics || []).length === 0 && (
        <p style={{ color: "#64748b", fontStyle: "italic" }}>No topics yet.</p>
      )}

      {(lesson.topics || []).map((topic, idx) => (
        <section key={topic.id} style={{ marginBottom: 32 }} data-testid={`public-topic-${topic.id}`}>
          <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 10 }}>
            {idx + 1}. {topic.title}
          </h2>
          {topic.body && (
            <div
              className="ql-rendered"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(topic.body) }}
            />
          )}
          {(topic.tasks || []).length > 0 && (
            <ol style={{ marginTop: 12, paddingLeft: 24 }}>
              {topic.tasks.map((task) => (
                <li key={task.id} style={{ marginBottom: 4 }} data-testid={`public-task-${task.id}`}>
                  {task.content || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Untitled task</span>}
                </li>
              ))}
            </ol>
          )}
        </section>
      ))}
    </div>
  );
}
